import { DateTime, type WeekdayNumbers, type WeekNumbers } from 'luxon'

import type { SessionDetails } from '@/components/Worlds/types'

// The minimum percentage of weeks spanned by the sessions that a session start time must show up in to be considered 'typical'
const TYPICALITY_THRESHOLD = 50

export enum Repeats {
  Weekly = 'Weekly',
  Daily = 'Daily',
  Weekdays = 'Weekdays',
}

export type TypicalStartTime = {
  repeats: Repeats
  sampleTime: DateTime
}

const DAY_AND_TIME_FORMAT = 'c-HH-mm'
const TIME_FORMAT = 'c-HH-mm'

export default function findScheduleAmongSessions(
  sessions: Pick<SessionDetails, 'startDate'>[]
): TypicalStartTime[] {
  // Step 1: Look for weekly patterns

  const weekStartTimesCount: Record<
    string,
    { weekNumbers: Set<WeekNumbers>; originalDate: DateTime }
  > = {}
  const weeksSpanned = new Set()

  // Calculate the week numbers for each session and count the start times per week
  sessions.forEach(({ startDate }) => {
    const { weekNumber } = startDate
    const startTimeString = startDate.toFormat(DAY_AND_TIME_FORMAT)

    weeksSpanned.add(weekNumber)

    if (!(startTimeString in weekStartTimesCount)) {
      weekStartTimesCount[startTimeString] = { weekNumbers: new Set(), originalDate: startDate }
    }

    weekStartTimesCount[startTimeString].weekNumbers.add(weekNumber)
  })

  const totalWeeks = weeksSpanned.size
  const threshold = (TYPICALITY_THRESHOLD / 100) * totalWeeks

  const typicalWeeklyStartTimes = Object.keys(weekStartTimesCount)
    .filter(startTime => weekStartTimesCount[startTime].weekNumbers.size >= threshold)
    .map(startTime => weekStartTimesCount[startTime].originalDate)

  // Step 2: Look for daily & weekdays patterns

  const dayStartTimesCount: Record<string, Set<WeekdayNumbers>> = {}

  typicalWeeklyStartTimes.forEach(startTime => {
    const { weekday } = startTime
    const startTimeString = startTime.toFormat(TIME_FORMAT)

    if (!(startTimeString in dayStartTimesCount)) {
      dayStartTimesCount[startTimeString] = new Set()
    }

    dayStartTimesCount[startTimeString].add(weekday)
  })

  // Step 3: Bring the patterns together

  const typicalStartTimes: TypicalStartTime[] = []

  // A dictionary of start times that repeat daily or on weekdays
  const dailyOrWeekdaysStartTimes: Record<string, boolean> = {}

  Object.keys(dayStartTimesCount).forEach(time => {
    const weekdays = dayStartTimesCount[time]
    if (weekdays.size === 7) {
      typicalStartTimes.push({
        repeats: Repeats.Daily,
        sampleTime: DateTime.fromFormat(time, TIME_FORMAT),
      })
      dailyOrWeekdaysStartTimes[time] = true
    } else if (
      weekdays.has(1) &&
      weekdays.has(2) &&
      weekdays.has(3) &&
      weekdays.has(4) &&
      weekdays.has(5)
    ) {
      typicalStartTimes.push({
        repeats: Repeats.Weekdays,
        sampleTime: DateTime.fromFormat(time, 'HH-mm'),
      })
      dailyOrWeekdaysStartTimes[time] = true
    }
  })

  for (const typicalWeeklyStartTime of typicalWeeklyStartTimes) {
    const startTimeString = typicalWeeklyStartTime.toFormat(TIME_FORMAT)
    if (!(startTimeString in dailyOrWeekdaysStartTimes)) {
      typicalStartTimes.push({
        repeats: Repeats.Weekly,
        sampleTime: typicalWeeklyStartTime,
      })
    }
  }

  typicalStartTimes.sort((a, b) => a.sampleTime.diff(b.sampleTime).as('milliseconds'))

  return typicalStartTimes
}
